import { useQuery } from "@tanstack/react-query";
import {
  generatePath,
  Navigate,
  useNavigate,
  useParams,
} from "react-router-dom";
import { t } from "ttag";

import { ScreenLayout } from "@/app/components/layouts";
import { ErrorState } from "@/app/components/legacy-components";
import { ROUTES } from "@/app/router";
import { formQueriesStore } from "@/features/forms/api";
import { DynamicForm } from "@/features/forms/components";
import { useSubmitIntake } from "@/features/forms/hooks";
import type { Answers } from "@/features/forms/models";
import { DisqualifiedModal } from "@/features/intake-form/components/disqualified-modal";
import { WhiteCoatSyncModal } from "@/features/intake-form/components/white-coat-sync-modal";
import { INTAKE_FORM_ROUTE } from "@/features/intake-form/router";
import { PriorConsultationRequired } from "@/features/intake-form/screens";
import { LogoLoader } from "@/shared/components";
import { SUBSCRIPTION_VENDOR } from "@/shared/domains";
import { useBoolean } from "@/shared/hooks";
import { REFILL_TYPE, refillTypeSchema } from "@/shared/models";
import { useIntakeFormContext } from "../context";

export const AssessmentForm = () => {
  const params = useParams();
  const parsedState = refillTypeSchema.safeParse(params.userPreference);

  const userPreference = parsedState.success ? parsedState.data : undefined;

  const { subscription, completed } = useIntakeFormContext();

  const {
    data: intakeForm,
    isLoading,
    isSuccess,
    isError,
  } = useQuery(formQueriesStore.intake(subscription.currentProduct.id));

  const {
    value: showPriorConsultationRequired,
    setTrue: openPriorConsultationRequired,
    setFalse: closePriorConsultationRequired,
  } = useBoolean();

  const {
    value: showWhiteCoatModal,
    setTrue: openWhiteCoatModal,
    setFalse: closeWhiteCoatModal,
  } = useBoolean();

  const {
    value: showDisqualifiedModal,
    setTrue: openDisqualifiedModal,
    setFalse: closeDisqualifiedModal,
  } = useBoolean();

  const submitMutation = useSubmitIntake();

  const navigate = useNavigate();

  const isFromWhiteCoat =
    subscription.vendor === SUBSCRIPTION_VENDOR.WHITE_COAT;

  if (isError) {
    return <ErrorState className="-translate-y-10 border-none" />;
  }

  const handleSubmit = (answers: Answers) => {
    if (intakeForm?.formId && userPreference) {
      submitMutation.mutate(
        {
          userPreference,
          formId: intakeForm.formId,
          answers,
          productId: subscription.currentProduct.id,
        },
        {
          onSuccess: ({ needsSync, isDisqualified }) => {
            if (isDisqualified) {
              return openDisqualifiedModal();
            }

            if (userPreference === REFILL_TYPE.SYNC) {
              if (isFromWhiteCoat) {
                return openWhiteCoatModal();
              }

              return navigate(
                generatePath(INTAKE_FORM_ROUTE.SCHEDULE_VIDEO_CONSULTATION, {
                  productId: String(subscription.currentProduct.id),
                }),
              );
            }

            if (needsSync) {
              if (isFromWhiteCoat) {
                return openWhiteCoatModal();
              }

              return openPriorConsultationRequired();
            }

            return navigate(INTAKE_FORM_ROUTE.SUCCESS);
          },
        },
      );
    }
  };

  if (completed || !userPreference) {
    return <Navigate to={ROUTES.BASE} />;
  }

  return (
    <ScreenLayout
      hasCardHeader={false}
      title={t`Intake form questions`}
      description={t`(*) Mandatory field`}
    >
      {isSuccess && <DynamicForm form={intakeForm} onSubmit={handleSubmit} />}

      <LogoLoader
        show={isLoading || submitMutation.isPending}
        fullscreen={submitMutation.isPending}
      />

      <PriorConsultationRequired
        open={showPriorConsultationRequired}
        onOpenChange={closePriorConsultationRequired}
      />

      <WhiteCoatSyncModal
        open={showWhiteCoatModal}
        onOpenChange={closeWhiteCoatModal}
      />

      <DisqualifiedModal
        open={showDisqualifiedModal}
        onOpenChange={closeDisqualifiedModal}
      />
    </ScreenLayout>
  );
};
